import { supportsTradingMethods, parseTradingMethodCatalog, parseMethodEvidence, matchesMethodResult, METHOD_UNAVAILABLE, METHOD_RESULT_MISMATCH, TRADING_METHODS_CATALOG, type TradingMethodCatalog } from "./trading-methods.ts";
import { requireRunnableStrategy } from "./strategy-entry.ts";
import { simulationT, simulationCapitals, validSimulationRequest, validSimulationReport } from "./ftmo-simulation.ts";
import type {
  AccountUsage,
  BacktestAnalysis,
  BacktestFormState,
  BacktestResult,
  HistoryItem,
  LockedHoldoutResponse,
  PublicPlanCatalog,
  RealtimeBot,
} from "./types.ts";
import {
  AnalysisAPIError,
  AnalysisJobTerminalFailure,
  isSafeLegacyAnalysisFallback,
  retryAfterSeconds,
  type AnalysisJob,
  type AnalysisJobEnvelope,
  type BacktestAnalysisStart,
  type ServiceCapabilities,
} from "./analysis-jobs.ts";
import { API_BASE_URL, browserSafeFetch } from "./api-base.ts";
import { sharedPublicRead } from "./public-read-cache.ts";
import { parseMarketDataHistoryLimit } from "./market-data-error.ts";
import type { SharedStrategyAttribution } from "./shared-research-network.ts";
import {
  cloneStrategyRuleDSL,
  strategyRuleDSLIdentity,
  type StrategyRuleDSL,
} from "./strategy-builder.ts";

const buildEnvironment = (import.meta as ImportMeta & {
  env?: Record<string, string | undefined>;
}).env;

export const DEFAULT_MAX_BACKTEST_CANDLES = Number(
  buildEnvironment?.VITE_MAX_BACKTEST_CANDLES || 8640,
);

export { API_BASE_URL } from "./api-base.ts";

function preferredLanguage(locale?: string): string {
  return locale || (typeof navigator !== "undefined" ? navigator.language : "en") || "en";
}

export type AuthTokenSource = string | ((forceRefresh?: boolean) => Promise<string | null>);

export interface PendingSharedStrategyAttribution extends SharedStrategyAttribution {
  /** Full deterministic identity of the imported canonical v2 rule tree. */
  canonicalStrategyFingerprint: string;
  /** Defensive copy retained so a stale/mutated fingerprint also fails closed. */
  canonicalStrategyDSL: StrategyRuleDSL;
}

function safeStrategyRuleIdentity(value: StrategyRuleDSL | undefined): string {
  try {
    return strategyRuleDSLIdentity(value);
  } catch {
    return "";
  }
}

export function createPendingSharedStrategyAttribution(
  attribution: SharedStrategyAttribution,
  canonicalStrategyDSL: StrategyRuleDSL | undefined,
): PendingSharedStrategyAttribution | null {
  const canonicalStrategyFingerprint = safeStrategyRuleIdentity(canonicalStrategyDSL);
  if (!canonicalStrategyDSL || !canonicalStrategyFingerprint) return null;
  return {
    ...attribution,
    canonicalStrategyFingerprint,
    canonicalStrategyDSL: cloneStrategyRuleDSL(canonicalStrategyDSL),
  };
}

/**
 * Attribution is one-shot and only belongs to an unchanged imported builder
 * recipe. Switching mode, removing rules or editing even one canonical value
 * turns the next backtest into an ordinary run instead of sending stale IDs.
 */
export function sharedAttributionForBacktest(
  form: BacktestFormState,
  pending: PendingSharedStrategyAttribution | null,
): SharedStrategyAttribution | null {
  if (!pending || form.strategyMode !== "builder" || !form.strategyDSL) return null;
  const currentFingerprint = safeStrategyRuleIdentity(form.strategyDSL);
  const frozenFingerprint = safeStrategyRuleIdentity(pending.canonicalStrategyDSL);
  if (
    !currentFingerprint
    || currentFingerprint !== pending.canonicalStrategyFingerprint
    || frozenFingerprint !== pending.canonicalStrategyFingerprint
  ) return null;
  return {
    shareID: pending.shareID,
    attributionEventID: pending.attributionEventID,
  };
}

async function resolveToken(token?: AuthTokenSource | null, forceRefresh = false): Promise<string | null> {
  if (!token) return null;
  const resolved = typeof token === "function" ? await token(forceRefresh) : token;
  if (!resolved) {
    throw new AnalysisAPIError(401, "Your sign-in session is unavailable. Please sign in again.", {
      reason: "auth_token_unavailable",
    });
  }
  return resolved;
}

function resolveReadToken(token: AuthTokenSource, forceRefresh: boolean, signal: AbortSignal): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const abort = () => {
      signal.removeEventListener("abort", abort);
      reject(new DOMException("Request cancelled", "AbortError"));
    };
    if (signal.aborted) { abort(); return; }
    signal.addEventListener("abort", abort, { once: true });
    // Firebase token work cannot be cancelled, but an abandoned GET must
    // settle immediately and must never issue HTTP if its token arrives late.
    resolveToken(token, forceRefresh).then((value) => {
      signal.removeEventListener("abort", abort);
      if (signal.aborted) abort(); else resolve(value);
    }, (error: unknown) => {
      signal.removeEventListener("abort", abort);
      reject(error);
    });
  });
}

async function headers(token?: AuthTokenSource | null, locale?: string): Promise<HeadersInit> {
  const next: HeadersInit = {
    "Content-Type": "application/json",
    "Accept-Language": preferredLanguage(locale),
  };
  const resolvedToken = await resolveToken(token);
  if (resolvedToken) {
    next.Authorization = `Bearer ${resolvedToken}`;
  }
  return next;
}

async function requestJson<T>(
  path: string,
  init: RequestInit = {},
  locale?: string,
  token?: AuthTokenSource | null,
  forceRefreshFirst = false,
): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (init.signal?.aborted) controller.abort();
  init.signal?.addEventListener("abort", abort, { once: true });
  // Reads may be abandoned safely. Mutations retain their existing completion
  // semantics and are never replayed because a client deadline elapsed.
  const isRead = !init.method || init.method === "GET";
  const timeout = isRead ? setTimeout(abort, 30_000) : undefined;
  const perform = async (forceRefresh = false): Promise<Response> => {
    if (isRead) controller.signal.throwIfAborted();
    const requestHeaders = new Headers(init.headers);
    if (!requestHeaders.has("Accept-Language")) {
      requestHeaders.set("Accept-Language", preferredLanguage(locale));
    }
    if (token) {
      const bearer = isRead
        ? await resolveReadToken(token, forceRefresh, controller.signal)
        : await resolveToken(token, forceRefresh);
      requestHeaders.set("Authorization", `Bearer ${bearer}`);
    }
    if (isRead) controller.signal.throwIfAborted();
    return browserSafeFetch(apiURL(path), { ...init, headers: requestHeaders, signal: controller.signal });
  };

  try {
    let response = await perform(forceRefreshFirst);
    if (
      response.status === 401
      && typeof token === "function"
      && !forceRefreshFirst
    ) {
      // A revoked/expired cached Firebase bearer gets exactly one forced refresh.
      // Mutating requests remain safe because the server rejects authorization
      // before running account-scoped work.
      response = await perform(true);
    }
    const bodyText = await response.text();
    const body = parseResponseBody(bodyText);
    if (!response.ok) {
      throw new AnalysisAPIError(
        response.status,
        errorMessageFromBody(body, response.status),
        {
          reason: errorReasonFromBody(body),
          code: errorStringField(body, "code"),
          localizationKey: errorStringField(body, "localization_key"),
          arguments: errorArgumentsFromBody(body),
          localizedMessage: errorStringField(body, "localized_message"),
          marketDataHistoryLimit: parseMarketDataHistoryLimit(body),
          retryable: errorBooleanField(body, "retryable"),
          retryAfterSeconds: retryAfterSeconds(
            response.headers.get("Retry-After"),
          ),
        },
      );
    }
    return body as T;
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
    init.signal?.removeEventListener("abort", abort);
  }
}

function apiURL(path: string): string {
  const base = new URL(`${API_BASE_URL}/`);
  const target = new URL(path, base);
  if (target.origin !== base.origin) {
    throw new Error("The API returned an unsafe cross-origin endpoint.");
  }
  return target.toString();
}

function parseResponseBody(bodyText: string): any {
  if (!bodyText) return {};
  try {
    return JSON.parse(bodyText);
  } catch {
    return { message: bodyText };
  }
}

function errorMessageFromBody(body: any, status: number): string {
  const detail = body?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (detail?.reason) return detail.reason;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (typeof body?.message === "string") return body.message;
  return `Request failed: ${status}`;
}

function errorReasonFromBody(body: any): string | undefined {
  return errorStringField(body, "reason");
}

function errorStringField(body: any, field: string): string | undefined {
  const topLevel = body?.[field];
  if (typeof topLevel === "string" && topLevel.trim()) return topLevel;
  const nested = body?.detail?.[field];
  return typeof nested === "string" && nested.trim() ? nested : undefined;
}

function errorBooleanField(body: any, field: string): boolean | undefined {
  const topLevel = body?.[field];
  if (typeof topLevel === "boolean") return topLevel;
  const nested = body?.detail?.[field];
  return typeof nested === "boolean" ? nested : undefined;
}

function errorArgumentsFromBody(body: any): Record<string, string | number | boolean | null> | undefined {
  const candidate = body?.arguments || body?.detail?.arguments;
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) return undefined;
  return Object.fromEntries(
    Object.entries(candidate).filter(([, value]) => (
      value === null || ["string", "number", "boolean"].includes(typeof value)
    )),
  ) as Record<string, string | number | boolean | null>;
}

export function timeframeSeconds(timeframe: string): number {
  const match = timeframe.trim().toLowerCase().match(/^(\d+)([mhdw])$/);
  if (!match) return 0;
  const value = Number(match[1]);
  const unit = match[2];
  if (unit === "m") return value * 60;
  if (unit === "h") return value * 60 * 60;
  if (unit === "d") return value * 24 * 60 * 60;
  if (unit === "w") return value * 7 * 24 * 60 * 60;
  return 0;
}

export function maxDaysForTimeframe(timeframe: string, maxCandles = DEFAULT_MAX_BACKTEST_CANDLES): number {
  const seconds = timeframeSeconds(timeframe);
  if (seconds <= 0) return 1;
  return Math.max(1, Math.floor((maxCandles * seconds) / 86400));
}

export function effectiveMaxDaysForTimeframe(
  timeframe: string,
  maxCandles = DEFAULT_MAX_BACKTEST_CANDLES,
  maxPeriodDays?: number,
): number {
  const candleDays = maxDaysForTimeframe(timeframe, maxCandles);
  return Math.min(maxPeriodDays || candleDays, candleDays);
}

export function estimatedCandlesForRange(timeframe: string, startDate: Date, endDate: Date): number {
  const seconds = timeframeSeconds(timeframe);
  if (seconds <= 0) return 0;
  return Math.max(1, Math.ceil((endDate.getTime() - startDate.getTime()) / 1000 / seconds));
}

function clampedStartDate(timeframe: string, startDate: Date, endDate: Date, maxCandles: number, maxPeriodDays?: number): Date {
  const maxDays = effectiveMaxDaysForTimeframe(timeframe, maxCandles, maxPeriodDays);
  const earliest = new Date(endDate);
  earliest.setDate(earliest.getDate() - maxDays);
  return startDate < earliest ? earliest : startDate;
}

export function backtestDateRange(
  form: BacktestFormState,
  maxCandles: number,
  maxPeriodDays?: number,
  referenceDate = new Date(),
): BacktestDateRange {
  const period = form.period;
  const restored = form.restoredDateRange;
  if (period === "Custom" && restored && form.startDate === restored.inputStart && form.endDate === restored.inputEnd) {
    const start = new Date(restored.start), end = new Date(restored.end);
    if (Number.isFinite(start.getTime()) && Number.isFinite(end.getTime()) && start <= end
      && start >= clampedStartDate(form.timeframe, start, end, maxCandles, maxPeriodDays)) {
      return { start_date: restored.start, end_date: restored.end };
    }
  }
  if (period === "Custom") {
    const endDate = form.endDate ? new Date(`${form.endDate}T23:59:59`) : new Date(referenceDate);
    endDate.setHours(23, 59, 59, 0);
    const defaultStart = new Date(endDate);
    defaultStart.setDate(defaultStart.getDate() - effectiveMaxDaysForTimeframe(form.timeframe, maxCandles, maxPeriodDays));
    defaultStart.setHours(0, 0, 0, 0);
    const requestedStart = form.startDate ? new Date(`${form.startDate}T00:00:00`) : defaultStart;
    const startDate = clampedStartDate(form.timeframe, requestedStart, endDate, maxCandles, maxPeriodDays);
    startDate.setHours(0, 0, 0, 0);
    return {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    };
  }
  // Quick ranges use stable day boundaries so saving and reopening the same
  // setup can reproduce the exact request (and qualify for a free rerun).
  const now = new Date(referenceDate);
  now.setHours(23, 59, 59, 0);
  const start = new Date(now);
  if (period === "1M") start.setMonth(start.getMonth() - 1);
  if (period === "3M") start.setMonth(start.getMonth() - 3);
  if (period === "6M") start.setMonth(start.getMonth() - 6);
  if (period === "1Y") start.setFullYear(start.getFullYear() - 1);
  start.setHours(0, 0, 0, 0);
  const clampedStart = clampedStartDate(form.timeframe, start, now, maxCandles, maxPeriodDays);
  clampedStart.setHours(0, 0, 0, 0);
  return {
    start_date: clampedStart.toISOString(),
    end_date: now.toISOString(),
  };
}

export interface BacktestDateRange {
  start_date?: string;
  end_date?: string;
}

export function promptForForm(form: BacktestFormState): string {
  if (form.strategyMode === "method") return `Research method: ${form.strategyMethod?.method_id ?? "unknown"} v${form.strategyMethod?.method_version ?? "unknown"}`;
  if (form.strategyMode === "builder") {
    return "Visual Strategy Builder";
  }
  if (form.strategyMode === "manual") {
    return form.prompt.trim();
  }
  const symbol = form.symbol.toUpperCase();
  switch (form.strategyName) {
    case "Pullback":
      return `Buy pullback near EMA 9 and EMA 21 on ${symbol} and sell when trend weakens`;
    case "Breakout":
      return `Buy breakout above previous high on ${symbol} and sell below previous low`;
    case "RSI":
      return `Buy RSI oversold recovery on ${symbol} and sell when RSI leaves overbought`;
    case "Bollinger":
      return `Buy Bollinger lower band recovery on ${symbol} and sell near the middle band`;
    case "MACD":
      return `Buy MACD bullish signal crossover on ${symbol} and sell on bearish signal crossover`;
    case "Engulfing":
      return `Buy bullish engulfing price action on ${symbol} and sell below previous low`;
    case "Pin Bar":
      return `Buy bullish pin bar price action on ${symbol} and sell below previous low`;
    case "Inside Bar":
      return `Buy inside bar breakout on ${symbol} and sell below previous low`;
    case "Sweep":
      return `Buy liquidity sweep reclaim on ${symbol} and sell below previous low`;
    case "Trend+":
      return `Run Trend Pullback Momentum strategy on ${symbol} using EMA trend filter, RSI recovery, MACD histogram momentum, ATR stop, and 1:2 reward risk`;
    case "Vol Break":
      return `Run Volume Breakout strategy on ${symbol} using previous high breakout, volume confirmation, ATR filter, ATR stop, and 1:2.5 reward risk`;
    case "Mean+":
      return `Run Advanced Mean Reversion strategy on ${symbol} using RSI, EMA trend filter, ATR stop, and mean reversion exit`;
    case "MTF":
      return `Run Multi-Timeframe trend proxy strategy on ${symbol} using long EMA trend, short EMA alignment, RSI timing, ATR stop, and 1:2 reward risk`;
    default:
      return `Buy MA 9 21 crossover for ${symbol} and sell when fast MA crosses below slow MA`;
  }
}

function backtestPayload(
  form: BacktestFormState,
  locale?: string,
  maxCandles = DEFAULT_MAX_BACKTEST_CANDLES,
  maxPeriodDays?: number,
  historyItemID?: string | null,
  preparedDateRange?: BacktestDateRange,
  sharedAttribution?: SharedStrategyAttribution | null,
) {
  const prompt = promptForForm(form);
  if (form.ftmoSimulation) historyItemID = null;
  const provider = form.provider ?? backtestProviderForSymbol(form.symbol);
  return {
    prompt,
    symbol: form.symbol.toUpperCase(),
    timeframe: form.timeframe,
    initial_capital: form.initialCapital,
    commission_rate: form.commissionRate / 100,
    slippage_bps: form.slippageBps,
    position_size_pct: form.positionSizePct / 100,
    execution_model: form.executionModel,
    margin_mode: form.marginMode,
    leverage: form.executionModel === "futures" ? form.leverage : 1,
    maintenance_margin_rate: form.maintenanceMarginPercent / 100,
    funding_rate_pct: form.executionModel === "futures" ? form.fundingRatePercent / 100 : 0,
    funding_interval_hours: form.fundingIntervalHours,
    liquidation_fee_rate: form.liquidationFeePercent / 100,
    trade_direction: form.tradeDirection,
    stop_loss_pct: form.stopLossPct / 100,
    take_profit_pct: form.takeProfitPct / 100,
    exit_mode: form.exitMode,
    use_ai: form.useAI,
    preferred_language: preferredLanguage(locale),
    market: form.market ?? backtestMarketForSymbol(form.symbol),
    provider,
    strategy_constraints: {
      allowed_indicators: ["ema", "sma", "rsi", "macd", "bollinger", "atr", "volume"],
      allow_short: form.tradeDirection !== "long",
      risk_model: form.strategyMode === "method" ? "strategy" : "fixed_percent",
      max_indicators: 5,
      mode: form.strategyMode === "method" ? "trading_method" : form.strategyMode,
    },
    // Additive evidence is explicitly requested by the v1.6 web client. Older
    // servers ignore the unknown field and retain their released response.
    include_research_evidence: true,
    ...(form.strategyMode === "method" ? { strategy_method: form.strategyMethod, risk_mode: "strategy", exit_mode: "tp_sl_only", use_ai: false } : {}),
    ...(historyItemID ? { history_item_id: historyItemID } : {}),
    ...(form.ftmoSimulation ? { ftmo_simulation: form.ftmoSimulation } : {}),
    ...(form.strategyMode === "builder" && form.strategyDSL
      ? { strategy_dsl: form.strategyDSL }
      : {}),
    ...(preparedDateRange || backtestDateRange(form, maxCandles, maxPeriodDays)),
    ...(sharedAttribution ? {
      source_share_id: sharedAttribution.shareID,
      attribution_event_id: sharedAttribution.attributionEventID,
    } : {}),
  };
}

export function backtestProviderForSymbol(symbol: string): string {
  const normalized = symbol.toUpperCase();
  if (/^[A-Z0-9]+(USDT|USDC|BTC|ETH|BNB|FDUSD)$/.test(normalized)) return "binance";
  return "yfinance";
}

export function backtestMarketForSymbol(symbol: string): string {
  const normalized = symbol.toUpperCase();
  if (backtestProviderForSymbol(normalized) === "binance") return "crypto";
  if (normalized.includes("=X") || normalized.includes("/")) return "forex";
  if (normalized.includes("=F")) return "futures";
  if (normalized.startsWith("^")) return "index";
  return "equity";
}

export async function runBacktest(
  form: BacktestFormState,
  token?: AuthTokenSource | null,
  locale?: string,
  maxCandles = DEFAULT_MAX_BACKTEST_CANDLES,
  maxPeriodDays?: number,
  historyItemID?: string | null,
  preparedDateRange?: BacktestDateRange,
  sharedAttribution?: SharedStrategyAttribution | null,
): Promise<BacktestResult> {
  const runnable = requireRunnableStrategy(form);
  if (runnable.ftmoSimulation) {
    if (!validSimulationRequest(runnable.ftmoSimulation) || runnable.strategyMode !== "method" || !simulationCapitals(runnable.ftmoSimulation.product).includes(runnable.initialCapital)) {
      throw new AnalysisAPIError(422, simulationT(locale ?? "en", "methodRequired"), { reason: "ftmo_simulation_invalid_setup" });
    }
    const capabilities = await requestJson<{ simulation?: { enabled: boolean; schema_version: number } }>("/api/v1/ftmo/capabilities", { headers: await headers(null, locale), cache: "no-store" }, locale);
    if (capabilities.simulation?.enabled !== true || capabilities.simulation.schema_version !== 1) {
      throw new AnalysisAPIError(503, simulationT(locale ?? "en", "unsupported"), { reason: "ftmo_simulation_unavailable" });
    }
  }
  if (runnable.strategyMode === "method") {
    // Fresh handshake on every run, including saved reruns. Never let an old
    // server ignore the new field and execute the display prompt as a preset.
    const catalog = await fetchTradingMethods(locale, undefined, true);
    if (!catalog.methods.some((m) => m.method_id === runnable.strategyMethod!.method_id && m.method_version === runnable.strategyMethod!.method_version)) throw new Error(METHOD_UNAVAILABLE);
  }
  const payload = backtestPayload(
    runnable,
    locale,
    maxCandles,
    maxPeriodDays,
    historyItemID,
    preparedDateRange,
    sharedAttribution,
  );
  const response = await requestJson<BacktestResult>("/api/backtest/run", {
    method: "POST",
    headers: await headers(null, locale),
    body: JSON.stringify(payload),
  }, locale, token, Boolean(token && typeof token === "function"));
  if (runnable.strategyMode === "method") {
    const evidence = parseMethodEvidence(response.method_evidence);
    if (!matchesMethodResult(runnable.strategyMethod!, evidence)) throw new Error(METHOD_RESULT_MISMATCH);
    response.method_evidence = evidence;
  }
  if (token && response.usage?.plan === "anonymous") {
    throw new AnalysisAPIError(401, "Your sign-in session expired before the backtest completed.", {
      reason: "authenticated_request_downgraded",
    });
  }
  if (runnable.ftmoSimulation && (!validSimulationReport(response.ftmo_simulation) || response.ftmo_simulation.product !== runnable.ftmoSimulation.product || Number(response.ftmo_simulation.initial_capital) !== runnable.initialCapital)) {
    throw new AnalysisAPIError(502, simulationT(locale ?? "en", "unknown"), { reason: "ftmo_simulation_result_mismatch" });
  }
  return response;
}

export async function fetchRealtimeBots(token: AuthTokenSource, locale?: string, signal?: AbortSignal): Promise<{ bots: RealtimeBot[] }> {
  return requestJson<{ bots: RealtimeBot[] }>("/api/bots", {
    signal,
    headers: await headers(null, locale),
  }, locale, token);
}

export async function createRealtimeBot(
  form: BacktestFormState,
  token: AuthTokenSource,
  locale?: string,
  maxCandles = DEFAULT_MAX_BACKTEST_CANDLES,
  maxPeriodDays?: number,
): Promise<{ bot: RealtimeBot }> {
  if (form.strategyMode === "method" || form.strategyMethod) throw new Error(METHOD_UNAVAILABLE);
  return requestJson<{ bot: RealtimeBot }>("/api/bots", {
    method: "POST",
    headers: await headers(null, locale),
    body: JSON.stringify({
      name: form.strategyMode === "auto"
        ? `${form.strategyName} ${form.symbol.toUpperCase()} ${form.timeframe}`
        : `${form.symbol.toUpperCase()} ${form.timeframe}`,
      interval_seconds: botIntervalSeconds(form.timeframe),
      request: backtestPayload(requireRunnableStrategy(form), locale, maxCandles, maxPeriodDays),
    }),
  }, locale, token);
}

export async function tickRealtimeBot(id: string, token: AuthTokenSource, locale?: string): Promise<{ bot: RealtimeBot }> {
  return requestJson<{ bot: RealtimeBot }>(`/api/bots/${encodeURIComponent(id)}/tick`, {
    method: "POST",
    headers: await headers(null, locale),
  }, locale, token);
}

export async function deleteRealtimeBot(id: string, token: AuthTokenSource, locale?: string): Promise<{ status: string }> {
  return requestJson<{ status: string }>(`/api/bots/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: await headers(null, locale),
  }, locale, token);
}

function botIntervalSeconds(timeframe: string): number {
  const seconds = timeframeSeconds(timeframe);
  if (seconds <= 0) return 900;
  return Math.max(300, Math.min(seconds, 86400));
}

export async function fetchUsage(token: AuthTokenSource, locale?: string, signal?: AbortSignal): Promise<AccountUsage> {
  const response = await requestJson<AccountUsage>("/api/account/usage", {
    signal,
    headers: await headers(null, locale),
  }, locale, token, typeof token === "function");
  if (response.plan === "anonymous") {
    throw new AnalysisAPIError(401, "Your sign-in session expired while loading usage.", {
      reason: "authenticated_request_downgraded",
    });
  }
  return response;
}

export async function fetchAnonymousUsage(locale?: string): Promise<AccountUsage> {
  return requestJson<AccountUsage>("/api/account/usage", {}, locale);
}

export async function fetchPublicPlans(locale?: string): Promise<PublicPlanCatalog> {
  return requestJson<PublicPlanCatalog>("/api/plans", {}, locale);
}

export async function fetchHistory(token: AuthTokenSource, locale?: string, signal?: AbortSignal): Promise<{ items: HistoryItem[]; usage?: AccountUsage }> {
  return requestJson<{ items: HistoryItem[]; usage?: AccountUsage }>("/api/account/history", {
    signal,
    headers: await headers(null, locale),
  }, locale, token);
}

export async function syncHistory(
  token: AuthTokenSource,
  items: HistoryItem[],
  locale?: string,
): Promise<{ items: HistoryItem[]; usage?: AccountUsage }> {
  return requestJson<{ items: HistoryItem[]; usage?: AccountUsage }>("/api/account/history", {
    method: "PUT",
    headers: await headers(null, locale),
    body: JSON.stringify({ items, replace: false }),
  }, locale, token);
}

export async function analyzeBacktest(
  result: BacktestResult,
  originalPrompt: string,
  token: AuthTokenSource,
  locale?: string,
): Promise<BacktestAnalysis> {
  return requestJson<BacktestAnalysis>("/api/backtest/analyze", {
    method: "POST",
    headers: await headers(null, locale),
    body: JSON.stringify({
      original_prompt: originalPrompt,
      result,
      use_ai: true,
      preferred_language: preferredLanguage(locale),
    }),
  }, locale, token);
}

/** Public, versioned catalog. The only URL fetched is the client-owned route. */
export async function fetchTradingMethods(locale?: string, signal?: AbortSignal, fresh = false): Promise<TradingMethodCatalog> {
  const capabilities = fresh
    ? await requestJson<unknown>("/api/capabilities", { signal, cache: "no-store" }, locale)
    : await fetchCapabilities(locale, signal);
  if (!supportsTradingMethods(capabilities)) throw new Error(METHOD_UNAVAILABLE);
  const catalog = parseTradingMethodCatalog(await requestJson<unknown>(TRADING_METHODS_CATALOG, { signal, cache: "no-store" }, locale));
  if (!catalog) throw new Error(METHOD_UNAVAILABLE);
  const methods = catalog.methods.filter((method) => supportsTradingMethods(capabilities, method));
  if (!methods.length) throw new Error(METHOD_UNAVAILABLE);
  return { ...catalog, methods };
}

export async function fetchCapabilities(locale?: string, signal?: AbortSignal, force = false): Promise<ServiceCapabilities> {
  return sharedPublicRead("/api/capabilities", preferredLanguage(locale),
    (sharedSignal) => requestJson<ServiceCapabilities>("/api/capabilities", { signal: sharedSignal }, locale), { signal, force });
}

export type MarketSymbolSearchResponse = {
  schema_version: number;
  catalog_version: string;
  query: string;
  items: Array<{
    symbol: string;
    displayName: string;
    group: string;
    assetClass: string;
  }>;
};

export async function fetchMarketSymbols(
  query: string,
  locale?: string,
): Promise<MarketSymbolSearchResponse> {
  const response = await requestJson<{
    schema_version: number;
    catalog_version: string;
    query: string;
    items: Array<{
      symbol: string;
      display_name: string;
      group: string;
      asset_class: string;
    }>;
  }>(`/api/v2/market-symbols?query=${encodeURIComponent(query)}&limit=100`, {}, locale);
  return {
    ...response,
    items: response.items.map((item) => ({
      symbol: item.symbol,
      displayName: item.display_name,
      group: item.group,
      assetClass: item.asset_class,
    })),
  };
}

export async function startBacktestAnalysis(
  result: BacktestResult,
  originalPrompt: string,
  idempotencyKey: string,
  token: AuthTokenSource,
  locale?: string,
): Promise<BacktestAnalysisStart> {
  let capabilities: ServiceCapabilities | null = null;
  try {
    capabilities = await fetchCapabilities(locale);
  } catch {
    // Capability discovery has no side effects. A rolling/older deployment or
    // transient discovery failure can safely use the released synchronous API.
    return {
      kind: "completed",
      analysis: await analyzeBacktest(
        result,
        originalPrompt,
        token,
        locale,
      ),
    };
  }

  const resumable = capabilities.capabilities?.resumable_analysis_jobs === true;
  if (!resumable) {
    return {
      kind: "completed",
      analysis: await analyzeBacktest(
        result,
        originalPrompt,
        token,
        locale,
      ),
    };
  }

  const endpoint = capabilities.capabilities?.analysis_jobs_endpoint
    || "/api/v2/backtest/analyze/jobs";
  try {
    const response = await requestJson<AnalysisJobEnvelope>(endpoint, {
      method: "POST",
      headers: {
        ...await headers(null, locale),
        "X-Idempotency-Key": idempotencyKey,
      },
      body: JSON.stringify({
        original_prompt: originalPrompt,
        result,
        use_ai: true,
        preferred_language: preferredLanguage(locale),
      }),
    }, locale, token);
    if (response.job.status === "failed") {
      throw new AnalysisJobTerminalFailure(response.job);
    }
    if (response.job.status === "completed") {
      if (!response.job.result) {
        throw new AnalysisJobTerminalFailure({
          ...response.job,
          error: {
            reason: "analysis_job_result_missing",
            message: "The completed AI analysis result is unavailable.",
            retryable: true,
          },
        });
      }
      return { kind: "completed", analysis: response.job.result };
    }
    return { kind: "pending", job: response.job };
  } catch (error) {
    if (
      error instanceof AnalysisAPIError
      && isSafeLegacyAnalysisFallback(
        error.status,
        error.reason,
        error.message,
      )
    ) {
      return {
        kind: "completed",
        analysis: await analyzeBacktest(
          result,
          originalPrompt,
          token,
          locale,
        ),
      };
    }
    throw error;
  }
}

export async function fetchBacktestAnalysisJob(
  job: Pick<AnalysisJob, "id" | "poll_url">,
  token: AuthTokenSource,
  locale?: string,
): Promise<AnalysisJob> {
  const endpoint = job.poll_url
    || `/api/v2/backtest/analyze/jobs/${encodeURIComponent(job.id)}`;
  const response = await requestJson<AnalysisJobEnvelope>(endpoint, {
    headers: await headers(null, locale),
  }, locale, token);
  if (response.job.status === "failed") {
    throw new AnalysisJobTerminalFailure(response.job);
  }
  if (response.job.status === "completed" && !response.job.result) {
    throw new AnalysisJobTerminalFailure({
      ...response.job,
      error: {
        reason: "analysis_job_result_missing",
        message: "The completed AI analysis result is unavailable.",
        retryable: true,
      },
    });
  }
  return response.job;
}

export async function evaluateLockedHoldout(
  trialId: string,
  token: AuthTokenSource,
  locale?: string,
): Promise<LockedHoldoutResponse> {
  return requestJson<LockedHoldoutResponse>("/api/backtest/analyze/locked-holdout", {
    method: "POST",
    headers: await headers(null, locale),
    body: JSON.stringify({
      trial_id: trialId,
      ...(locale ? { preferred_language: locale } : {}),
    }),
  }, locale, token);
}
